import React, { useState, useEffect, useRef, useMemo } from "react";
import "../css/checkout.scss";
import { useLocation } from "react-router-dom";
import ReactFlagsSelect from "react-flags-select";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { BASE_URL, LIST_URL } from "../constans/ApiEndpointConstants";
import { sendMessage } from "../apis/SendMessageAPI";
import { getUrl } from "../apis/GetPkAPI";
import { PixelWithEvent } from "./common/PixelWithEvent ";
import * as FacebookAPI from "../apis/FacebookAPI";
import Cookies from "js-cookie";
import * as GetPixelAPI from "../apis/GetPixelAPI";
import LocalStorageUtil from "../utils/localStorageUtil";
import germanMetadata from "german-metadata";
import CheckoutHeader from "./common/CheckoutHeader";
import { getOrderList } from "../apis/GetOrderList";
import headerLogo from "../assets/head/ico-new.png";
import faviconLogo from "../assets/head/ico-new.ico";
import { donateDeepColor, shareColor } from "../constans/ColorConstants";
import { generateDonationUrl } from "../utils/urlUtils";

const Checkout = () => {
  const location = useLocation();
  const orderInfo = location.state?.orderInfo || null;
  const campaignDetails = location.state?.detailsData || null;
  const donation_id = campaignDetails?.id;
  const pkMessageSentRef = useRef(false);
  const iframeContainerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [iframeUrl, setIframeUrl] = useState("");
  const [showIframe, setShowIframe] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(720);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const storedAd = LocalStorageUtil.getAd();
  const ad = storedAd ?? "donation";
  const [pixelRes, setPixelRes] = useState(null);
  const [selectedItems, setSelectedItems] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = localStorage.getItem("selectedDonationItems");
      return raw ? JSON.parse(raw) : [];
    } catch (error) {
      console.error("Failed to parse selected donation items:", error);
      return [];
    }
  });

  // 给图片加上前缀
  const resolveUrl = (raw) => {
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;
    return `${LIST_URL.replace(/\/$/, "")}/${String(raw).replace(/^\//, "")}`;
  };

  const handleItemToggle = (item) => {
    setSelectedItems((prev) => {
      const exists = prev.some(
        (i) => i.title === item.title && i.price === item.price
      );

      return exists
        ? prev.filter((i) => i.title !== item.title || i.price !== item.price)
        : [...prev, item];
    });
  };

  const savedAmounts = useMemo(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = localStorage.getItem("checkoutAmounts");
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.error("Failed to parse checkout amounts:", error);
      return null;
    }
  }, []);

  const [donationInput, setDonationInput] = useState(
    () => savedAmounts?.donationInput ?? "0.00"
  );
  const [tipPercent, setTipPercent] = useState(
    () => savedAmounts?.tipPercent ?? 1
  );
  const [tipMode, setTipMode] = useState(
    () => savedAmounts?.tipMode ?? "slider"
  );
  const [customTip, setCustomTip] = useState(
    () => savedAmounts?.customTip ?? ""
  );

  // 动态捐款数据
  const [apiDonationsTotal, setApiDonationsTotal] = useState(() => {
    if (typeof window === "undefined") return 0;
    const stored = window.localStorage.getItem("apiDonationsTotal");
    const parsed = parseFloat(stored);
    return Number.isFinite(parsed) ? parsed : 0;
  });
  const raised = campaignDetails?.raised;
  const goal = campaignDetails?.goal;

  // 动态计算捐款总额：初始值 + API 捐款总额
  const totalRaised = useMemo(() => {
    return (raised ?? 0) + apiDonationsTotal;
  }, [raised, apiDonationsTotal]);

  // 动态计算进度百分比
  const progressPercentage = useMemo(() => {
    if (!goal || goal <= 0) return 0;
    const percentage = Math.round((totalRaised / goal) * 100);
    return Math.min(percentage, 100);
  }, [totalRaised, goal]);

  // 动态计算剩余需捐款金额
  const amountRemaining = useMemo(() => {
    const remaining = (goal ?? 0) - totalRaised;
    return Math.max(0, remaining);
  }, [goal, totalRaised]);

  // 进度条渐变 ID
  const progressGradientSeed = useMemo(
    () => Math.random().toString(36).slice(2, 11),
    []
  );
  const progressGradientId = `checkoutProgressGradient-${progressGradientSeed}`;

  //event_id，要去重，一天之内用一个，存cookie
  const getEventId = (eventType, auxiliaryProps) => {
    // 根据事件类型使用不同的cookie名
    let cookieName = `fb_event_id_${eventType}`;

    if (eventType === "CustomizeProduct") {
      if (auxiliaryProps) {
        cookieName = `fb_event_id_${eventType}_${auxiliaryProps}`;
      }
    }

    let storedEventId = Cookies.get(cookieName);
    if (storedEventId) {
      return storedEventId;
    } else {
      // 使用 10 位随机数字作为 event_id
      const newEventId = Math.round(Math.random() * 10000000000).toString();
      Cookies.set(cookieName, newEventId, { expires: 1 }); // 存储1天
      return newEventId;
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // 简单的美国手机号校验：允许 10 位，或以 1 开头的 11 位数字
  const isValidUSPhone = (value) => {
    if (!value) return false;
    const digits = String(value).replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) return true;
    if (digits.length === 10) return true;
    return false;
  };

  // 获取捐款列表计算动态数据
  useEffect(() => {
    const fetchOrderDonations = async () => {
      try {
        const response = await getOrderList(1);
        const orders = response?.data?.data ?? [];

        // 计算 API 捐款总额
        const apiTotal = orders.reduce((sum, order) => {
          let parsedData = {};
          if (typeof order?.data === "string") {
            try {
              parsedData = JSON.parse(order.data);
            } catch (e) {
              console.warn("Failed to parse order data:", e);
            }
          }
          const amount = Number(
            parsedData?.amount_fast ?? parsedData?.amount_fast ?? 0
          );
          return sum + (Number.isFinite(amount) && amount > 0 ? amount : 0);
        }, 0);

        setApiDonationsTotal(apiTotal);
        if (typeof window !== "undefined") {
          window.localStorage.setItem("apiDonationsTotal", apiTotal.toString());
        }
      } catch (error) {
        console.error("Failed to fetch order list:", error);
      }
    };

    fetchOrderDonations();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const snapshot = {
      donationInput,
      tipPercent,
      tipMode,
      customTip,
    };
    localStorage.setItem("checkoutAmounts", JSON.stringify(snapshot));
  }, [donationInput, tipPercent, tipMode, customTip]);

  useEffect(() => {
    const url = BASE_URL;
    const strippedUrl = url.replace(/^https?:\/\//, "");

    GetPixelAPI.getpixel(strippedUrl).then((res) => {
      const pixelApidata = res.data.data;
      setPixelRes(pixelApidata);
    });
  }, []);

  const usd = (v, options = {}) => {
    const value = Number(v) || 0;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: options.minimumFractionDigits ?? 2,
      maximumFractionDigits: options.maximumFractionDigits ?? 2,
    }).format(value);
  };

  const customTipOptions = [2.5, 5, 7.5];

  // derive top-donor percent tiers from campaign preset donateList if available
  const presetThresholds = useMemo(() => {
    const list = Array.isArray(campaignDetails?.donateList)
      ? campaignDetails.donateList
          .map((v) => Number(v))
          .filter((n) => Number.isFinite(n))
      : [];
    // sort ascending and dedupe
    const sorted = Array.from(new Set(list)).sort((a, b) => a - b);
    return sorted;
  }, [campaignDetails]);

  const getTopDonorPercent = (amount) => {
    const a = Number(amount) || 0;
    const t = presetThresholds;
    // If we have at least 5 thresholds, use the dynamic mapping:
    // second -> third : 15%
    // third -> fourth : 10%
    // fourth -> fifth : 5%
    // >= fifth : 1%
    if (t && t.length >= 5) {
      const second = t[1];
      const third = t[2];
      const fourth = t[3];
      const fifth = t[4];
      if (a > second && a <= third) return 15;
      if (a > third && a <= fourth) return 10;
      if (a > fourth && a <= fifth) return 5;
      if (a > fifth) return 1;
      return null;
    }

    // Fallback: simple static tiers if presets not available
    if (a >= 1000) return 1;
    if (a >= 500) return 2;
    if (a >= 250) return 5;
    if (a >= 100) return 10;
    return null;
  };

  // 固定金额捐款
  const donationAmount = Number(donationInput) || 0;

  // 物品捐赠金额
  const itemDonationAmount = useMemo(
    () =>
      selectedItems.reduce((total, item) => total + Number(item.price || 0), 0),
    [selectedItems]
  );

  // 验证是否有有效的捐款（固定金额或物品）
  const hasValidDonation = donationAmount > 0 || itemDonationAmount > 0;

  // 计算总捐款金额（不含小费）
  const totalDonationAmount = donationAmount + itemDonationAmount;

  // 基础捐款金额（用于计算小费比例）
  const baseDonationAmount = totalDonationAmount;

  const sliderTipAmount = Number(
    (baseDonationAmount * (tipPercent / 100)).toFixed(2)
  );
  const topDonorPercent = getTopDonorPercent(baseDonationAmount);
  const showTopDonorCelebration = topDonorPercent !== null;
  const customTipAmount = Number(customTip) || 0;
  const tipToggleLabel =
    tipMode === "slider" ? "Enter custom tip" : "Use suggested tip";
  const tipSliderDisabled = !hasValidDonation || tipMode !== "slider";
  const tipSliderFillPercent = baseDonationAmount ? (tipPercent / 30) * 100 : 0;
  const tipTickValues = Array.from({ length: 7 }, (_, index) => index * 5);
  const customTipPercent =
    baseDonationAmount > 0
      ? (customTipAmount / baseDonationAmount) * 100
      : null;
  const isCustomOptionActive = (percent) => {
    if (customTipPercent === null) return false;
    return Math.abs(customTipPercent - percent) < 0.25;
  };
  const showCustomTipGratitude =
    baseDonationAmount > 0 &&
    customTipOptions.some((percent) => isCustomOptionActive(percent));

  // 只有当预设阈值发生变化时，才重新匹配当前的捐款金额
  useEffect(() => {
    const num = parseFloat(donationInput);

    // If parseFloat returned a finite number > 0, use it to pick a preset or "custom"
    if (!Number.isNaN(num) && Number.isFinite(num) && num > 0) {
      if (presetThresholds.includes(num)) {
        setSelectedPreset(num);
      } else {
        setSelectedPreset("custom");
      }
      return;
    }

    // If the user is in the middle of typing a decimal like "." or ".5" (leading dot),
    // keep the input visible by treating it as "custom" in-progress input.
    // donationInput may be ".", ".5", "0.", "0.5", or similar while editing.
    const inProgressDecimal =
      /^\.?\d*$/.test(donationInput) && donationInput !== "";
    if (inProgressDecimal) {
      setSelectedPreset("custom");
      return;
    }

    // Otherwise clear the selection
    setSelectedPreset(null);
    // 只在 presetThresholds 变化时重新匹配，不监听 donationInput 的每次变化
  }, [presetThresholds]);

  const handlePresetClick = (amount) => {
    const isCustom = amount === "custom" || !Number.isFinite(Number(amount));
    const presetValue = isCustom ? "custom" : Number(amount);

    if (selectedPreset === presetValue) {
      // toggle off
      setSelectedPreset(null);
      setDonationInput("0.00");
      setTipMode("slider");
      setCustomTip("");
      return;
    }

    setSelectedPreset(presetValue);
    if (isCustom) {
      setDonationInput("0.00");
      triggerCustomizeProductEvent(0);
    } else {
      setDonationInput(presetValue.toFixed(2));
      triggerCustomizeProductEvent(presetValue);
    }
    setTipMode("slider");
    setCustomTip("");
  };

  const handleDonationInputChange = (e) => {
    const rawValue = e.target.value;
    const sanitized = rawValue
      .replace(/[^\d.]/g, "")
      .replace(/(\..*?)\..*/g, "$1");
    setDonationInput(sanitized);
  };

  const handleDonationBlur = () => {
    if (!donationAmount) {
      setDonationInput("0.00");
      setSelectedPreset(null);
      return;
    }

    // 格式化输入
    setDonationInput(donationAmount.toFixed(2));

    // 如果用户之前显式选择了 "Other"（custom），则保持 custom 状态，
    // 即使输入金额等于某个预设也不要切换上方按钮的高亮。
    if (selectedPreset !== "custom") {
      // 检查输入的金额是否匹配预设阈值（仅在未显式选择 Other 时生效）
      if (presetThresholds.includes(donationAmount)) {
        setSelectedPreset(donationAmount);
      } else {
        setSelectedPreset("custom");
      }
    }

    // 触发 CustomizeProduct 事件
    triggerCustomizeProductEvent(donationAmount);
  };

  const toggleTipMode = () => {
    if (tipMode === "slider") {
      const defaultTip = baseDonationAmount
        ? sliderTipAmount.toFixed(2)
        : donationInput
        ? donationInput
        : "";
      setCustomTip(defaultTip);
      setTipMode("custom");
      return;
    }

    setTipMode("slider");
    setCustomTip("");
  };

  const handleCustomTipChange = (e) => {
    const value = e.target.value
      .replace(/[^\d.]/g, "")
      .replace(/(\..*?)\..*/g, "$1");
    setCustomTip(value);
  };

  const handleCustomTipBlur = () => {
    if (!customTipAmount) {
      setCustomTip("");
      return;
    }
    setCustomTip(customTipAmount.toFixed(2));
  };

  const handleCustomTipOptionClick = (percent) => {
    if (!baseDonationAmount) {
      return;
    }
    const calculated = (baseDonationAmount * (percent / 100)).toFixed(2);
    setCustomTip(calculated);
  };

  // 随机生成六位字符（包含数字和字母）
  const generateRandomCode = () => {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < 6; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      result += characters[randomIndex];
    }
    return result;
  };

  // 获取或生成 userid
  let userid = localStorage.getItem("userid");
  if (!userid) {
    userid = generateRandomCode();
    localStorage.setItem("userid", userid);
  }

  // 触发 CustomizeProduct 事件
  const triggerCustomizeProductEvent = (amount) => {
    if (!amount || amount <= 0) {
      return;
    }

    // 调用 PixelWithEvent
    PixelWithEvent("CustomizeProduct", event_id);

    // 如果 pixelRes 已加载，触发 Facebook Pixel 事件
    if (pixelRes && pixelRes.pixel && pixelRes.fbqToken) {
      const event_id = getEventId("CustomizeProduct", amount);

      const contentIds = ["donation"];
      const numItems = 1;
      const contents = [
        {
          id: "donation",
          quantity: 1,
          title: "Donation",
        },
      ];

      const eventData = {
        content_ids: contentIds,
        content_name: "Donation",
        content_category: "Donation",
        value: parseFloat(amount),
        currency: "USD",
        num_items: numItems,
        contents: contents,
      };

      // 构建 userdata（可能不完整，但尽量使用已有信息）
      let userdata = {
        em: paymentInfo.email || "",
        ph: paymentInfo.phone || "",
        fn: paymentInfo.firstName || "",
        ln: paymentInfo.lastName || "",
        ct: paymentInfo.city || "",
        st: paymentInfo.state || "",
        zp: paymentInfo.zipCode || "",
        country: paymentInfo.country || "US",
        external_id: "",
        client_ip_address: "",
        client_user_agent: "",
        fbc: "",
        fbp: "",
        lead_id: "",
        anon_id: "",
        madid: "",
        page_id: "",
        page_scoped_user_id: "",
        ctwa_clid: "",
        ig_account_id: "",
        ig_sid: "",
      };

      // 触发 Facebook Pixel 事件
      FacebookAPI.Facebook(
        campaignDetails?.fbq_token,
        campaignDetails.pixel,
        event_id,
        "CustomizeProduct",
        eventData,
        userdata
      );
    }
  };

  let event_id = getEventId("payment");

  const [paymentInfo, setPaymentInfo] = useState(() => {
    // 优先从 orderDetail 读取之前保存的订单信息
    const savedOrderDetail = localStorage.getItem("orderDetail");
    if (savedOrderDetail) {
      try {
        const orderDetail = JSON.parse(savedOrderDetail);
        // 从 orderDetail 中提取用户信息
        return {
          email: orderDetail.email || "",
          firstName: orderDetail.firstName || "",
          lastName: orderDetail.lastName || "",
          zipCode: orderDetail.zipCode || "",
          state: orderDetail.state || "",
          city: orderDetail.city || "",
          address: orderDetail.address || "",
          phone: orderDetail.phone || "",
          country: orderDetail.country || "US",
          selected: orderDetail.country || "US",
        };
      } catch (error) {
        console.error("读取订单信息失败:", error);
      }
    }

    // 其次从 userPaymentInfo 读取之前保存的用户信息
    const savedUserInfo = localStorage.getItem("userPaymentInfo");
    if (savedUserInfo) {
      try {
        return JSON.parse(savedUserInfo);
      } catch (error) {
        console.error("读取用户信息失败:", error);
      }
    }

    // 如果没有保存的信息，返回默认值
    return {
      email: "",
      firstName: "",
      lastName: "",
      zipCode: "",
      state: "",
      city: "",
      address: "",
      phone: "",
      country: "US",
      selected: "US",
    };
  });
  // 是否由邮编自动填充了州/城市，如果是则不允许手动修改
  const [isLocationLocked, setIsLocationLocked] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({}); // 追踪哪些字段已经被触碰过
  const [formIsValid, setFormIsValid] = useState(false);
  const messageSentRef = useRef(false);
  const checkoutPageMessageSentRef = useRef(false);

  // 发送 Checkout Page 消息
  useEffect(() => {
    if (!checkoutPageMessageSentRef.current) {
      sendMessage(
        `${BASE_URL} [Checkout Page]:id=${donation_id}:ad=${ad}:userid=${userid}`
      );

      checkoutPageMessageSentRef.current = true;
    }
  }, [donation_id, ad, userid]);

  // 从订单信息构建 currentPlan
  useEffect(() => {
    if (orderInfo) {
      const plan = {
        id: orderInfo.id,
        name: orderInfo.name,
        totalPrice: orderInfo.totalPrice,
        pricePerUnit: orderInfo.basePrice,
        amount: orderInfo.quantity,
        discount: "0%",
        variant: orderInfo.variant,
        image: orderInfo.image,
      };
      setCurrentPlan(plan);
    }
  }, [orderInfo]);

  useEffect(() => {
    if (!messageSentRef.current && currentPlan) {
      sendMessage(
        `${BASE_URL} [${currentPlan.totalPrice} Checkout Page]:id=${donation_id}:ad=${ad}:userid=${userid}`
      );
      messageSentRef.current = true;
    }
  }, [currentPlan, donation_id, ad, userid]);

  useEffect(() => {
    const { email, firstName, lastName, zipCode, state, city, address, phone } =
      paymentInfo;

    const fieldsAreFilled =
      email &&
      firstName &&
      lastName &&
      zipCode &&
      state &&
      city &&
      address &&
      phone;

    const noErrors = Object.keys(errors).length === 0;
    const isValid = fieldsAreFilled && noErrors;

    setFormIsValid(isValid);
  }, [paymentInfo, errors]);

  // 当 iframe 显示时，自动滚动到 iframe 位置
  useEffect(() => {
    if (showIframe && iframeContainerRef.current) {
      setTimeout(() => {
        iframeContainerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 0);
    }
  }, [showIframe]);

  const validateEmail = (email) => {
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  //根据邮编自动填写城市和州
  const onPlzChange = (e) => {
    const rawValue = e.target.value;
    const sanitized = rawValue.replace(/[^\d]/g, "");

    setPaymentInfo((prev) => ({
      ...prev,
      zipCode: sanitized,
    }));

    // 如果邮编长度足够，尝试自动填充城市和州（可选功能）
    if (sanitized.length >= 5) {
      const cities = germanMetadata.citiesByPostalCode
        ? germanMetadata.citiesByPostalCode(sanitized)
        : null;

      if (cities && cities.length > 0) {
        const { name, state } = cities[0];
        setPaymentInfo((prev) => ({
          ...prev,
          zipCode: sanitized,
          city: name,
          state,
        }));
        // 找到有效 PLZ，并自动填充了州和城市 → 锁定 state/city
        setIsLocationLocked(true);

        // 清空与 PLZ / 城市 / 州相关的错误
        if (Object.keys(errors).length) {
          setErrors((prev) => {
            const updated = { ...prev };
            delete updated.zipCode;
            delete updated.city;
            delete updated.state;
            return updated;
          });
        }
      } else {
        // 如果找不到匹配的城市，解锁 state/city，但不显示错误（允许手动输入）
        setIsLocationLocked(false);
        if (errors.zipCode) {
          setErrors((prev) => {
            const updated = { ...prev };
            delete updated.zipCode;
            return updated;
          });
        }
      }
    } else {
      // 邮编长度不足时，解锁 state/city，并清除之前的 PLZ 相关错误
      setIsLocationLocked(false);
      if (errors.zipCode || errors.city || errors.state) {
        setErrors((prev) => {
          const updated = { ...prev };
          delete updated.zipCode;
          delete updated.city;
          delete updated.state;
          return updated;
        });
      }
    }
  };

  //输入框change
  const handlePaymentChange = (e) => {
    const { name, value } = e.target;

    // 当用户输入任何内容时（包括空格），清除该字段的错误状态
    if (errors[name]) {
      const newErrors = { ...errors };
      delete newErrors[name];
      setErrors(newErrors);
    }

    setPaymentInfo({
      ...paymentInfo,
      [name]: value,
    });
  };

  // 统一的 blur 校验处理函数
  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    let newErrors = { ...errors };

    // 标记该字段已被触碰
    setTouched((prev) => ({ ...prev, [name]: true }));

    switch (name) {
      case "email":
        if (!value.trim()) {
          newErrors.email = "Please enter your email address";
        } else {
          const emailError = validateEmail(value.trim());
          if (emailError) {
            newErrors.email = emailError;
          } else {
            delete newErrors.email;
          }
        }
        break;

      case "firstName":
        if (!value.trim()) {
          newErrors.firstName = "Please enter your first name";
        } else {
          delete newErrors.firstName;
        }
        break;

      case "lastName":
        if (!value.trim()) {
          newErrors.lastName = "Please enter your last name";
        } else {
          delete newErrors.lastName;
        }
        break;

      case "zipCode":
        if (!value) {
          newErrors.zipCode = "Please enter your postal code";
        } else {
          const zipNumbers = value.replace(/\D/g, ""); // 只保留数字
          if (zipNumbers.length === 0) {
            newErrors.zipCode = "Please enter a valid postal code";
          } else {
            // 邮编验证通过，清除错误
            delete newErrors.zipCode;
          }
        }
        break;

      case "state":
        // state 字段是必填的
        if (!value) {
          newErrors.state = "Please select a state";
        } else {
          delete newErrors.state;
        }
        break;

      case "city":
        if (!value.trim()) {
          newErrors.city = "Please enter your city";
        } else {
          delete newErrors.city;
        }
        break;

      case "address":
        if (!value.trim()) {
          newErrors.address = "Please enter your street and house number";
        } else {
          delete newErrors.address;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
  };

  //支付函数
  const handlePayNow = async () => {
    // 计算总价
    const totalDueToday =
      donationAmount +
      (tipMode === "slider" ? sliderTipAmount : customTipAmount);

    if (totalDueToday <= 0) {
      alert(
        "The total amount must be greater than 0. Please enter a donation amount."
      );
      return;
    }

    const { email, firstName, lastName, address, city, zipCode, state, phone } =
      paymentInfo;

    // 完整的字段校验
    const validationErrors = {};

    // 邮箱校验
    if (!email) {
      validationErrors.email = "Please enter your email address";
    } else {
      const emailError = validateEmail(email.trim());
      if (emailError) {
        validationErrors.email = emailError;
      }
    }

    // 电话号码校验（仅支持美国号码的简单长度校验）
    if (!phone) {
      validationErrors.phone = "Please enter your phone number";
    } else if (!isValidUSPhone(phone)) {
      validationErrors.phone = "Please enter a valid phone number";
    }

    // 姓名校验
    if (!firstName || !firstName.trim()) {
      validationErrors.firstName = "Please enter your first name";
    }
    if (!lastName || !lastName.trim()) {
      validationErrors.lastName = "Please enter your last name";
    }

    // 邮编校验
    if (!zipCode) {
      validationErrors.zipCode = "Please enter your postal code";
    } else {
      const zipNumbers = zipCode.replace(/\D/g, "");
      if (zipNumbers.length === 0) {
        validationErrors.zipCode = "Please enter a valid postal code";
      }
    }

    // 州校验
    if (!state) {
      validationErrors.state = "Please select a state";
    }

    // 城市校验
    if (!city) {
      validationErrors.city = "Please enter your city";
    } else if (!city.trim()) {
      validationErrors.city = "Please enter your city";
    }

    // 地址校验
    if (!address) {
      validationErrors.address = "Please enter your street and house number";
    } else if (!address.trim()) {
      validationErrors.address = "Please enter your street and house number";
    }

    if (Object.keys(validationErrors).length > 0) {
      // 标记有错误的字段为已触碰，这样才会显示红色边框
      const newTouched = {};
      Object.keys(validationErrors).forEach((key) => {
        newTouched[key] = true;
      });
      setTouched((prev) => ({ ...prev, ...newTouched }));
      setErrors(validationErrors);
      return;
    }

    try {
      setIsLoading(true);

      // 检查 pixelRes 是否已加载
      if (!pixelRes) {
        alert(
          "Payment configuration is loading. Please wait a moment and try again."
        );
        setIsLoading(false);
        return;
      }

      // 处理电话号码：只做基本清理（去除空格等），不统一转为德国号码
      const phoneNumberOnly = phone ? phone.replace(/\s+/g, "").trim() : "";

      // 计算总金额
      const tipAmount =
        tipMode === "slider" ? sliderTipAmount : customTipAmount;
      const totalDueToday = totalDonationAmount + tipAmount;

      // 构建基于捐款和 tip 的 goods 数据
      const goodsCandidate = {
        goods_title: "Donation", // 捐款名称
        goods_sku: "donation", // 捐款sku
        goods_price: totalDueToday, // 总金额（捐款 + tip）
        goods_image: "", // 不需要图片
        goods_num: 1, // 数量
      };

      const safeGoods = LocalStorageUtil.ensureGoods(goodsCandidate, {
        goods_title: "Donation",
        goods_sku: "donation",
        goods_price: totalDueToday || 50,
        goods_image: "",
        goods_num: 1,
      });

      const goodsJson = JSON.stringify(safeGoods);

      let orderDetail = {
        ad: LocalStorageUtil.getAd() || "donation",
        success_url: `${
          window.location.origin
        }/paysuccess?detailPath=${encodeURIComponent(
          generateDonationUrl(campaignDetails)
        )}?email=${encodeURIComponent(email.trim())}?phone=${encodeURIComponent(
          phone.trim()
        )}`,
        // success_url: "https://localhost:3000/paysuccess",
        cancel_url: `${window.location.origin}/payfial`,
        // cancel_url: "https://localhost:3000/payfial",
        back_url: `${BASE_URL}/checkout`, // 返回主页的URL
        locale: campaignDetails.locale, // 美国语言环境
        vehicleModel: campaignDetails.title, // 产品型号
        host: window.location.host || "", // 当前域名
        headerLogo: headerLogo || "", // 头部Logo
        faviconLogo: faviconLogo || "", // 网站图标
        successLogo: resolveUrl(campaignDetails?.image[0].url) || "", // 成功页面Logo
        successText: "Thank you for your donation!",
        indexTitle: campaignDetails.title,
        indexDesc: campaignDetails.title,
        brandColor: donateDeepColor || "#f16e40", // 品牌颜色
        otherInfo: pixelRes?.groupid || "", // 其他信息
        img: "", // 不需要商品图片
        title: campaignDetails
          ? generateDonationUrl(campaignDetails)
          : `/donationDetails/${campaignDetails?.id}`, // 捐款名称
        // TODO:
        goods: goodsJson, //传入转json格式的对象
        payment_intent_id: "",
        type: 10,
        curcrency: campaignDetails.currency_name,
        email: email.trim(),
        phone: phoneNumberOnly || "",
        state: state || "", // 州
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        snCode: "",
        city: city || "",
        zipCode: zipCode || "",
        address: address || "",
        detailAddress: "",
        country: paymentInfo.country || "US",
        id: "",
        orderNu: "",
        // TODO:
        tgid: pixelRes?.tgid || "",
        fbqToken: campaignDetails.fbq_token || "",
        pixel: campaignDetails.pixel || "",
        amount: parseFloat(totalDueToday) || 0,
        amount_fast: parseFloat(totalDueToday - tipAmount) || 0,
        // TODO:
        stusername: pixelRes?.stusername || "",
        fakeEmail: campaignDetails.email_id || "",
        donationID: campaignDetails?.id,
      };

      // 构建基于捐款的事件数据
      const contentIds = ["donation"];
      const numItems = 1;
      const contents = [
        {
          id: "donation",
          quantity: 1,
          title: "Donation",
        },
      ];

      let userdata = {
        em: orderDetail.email,
        ph: orderDetail.phone,
        fn: orderDetail.firstName,
        ln: orderDetail.lastName,
        ct: orderDetail.city,
        st: orderDetail.state,
        zp: orderDetail.zipCode,
        country: orderDetail.country,
        external_id: "",
        client_ip_address: "",
        client_user_agent: "",
        fbc: "",
        fbp: "",
        lead_id: "",
        anon_id: "",
        madid: "",
        page_id: "",
        page_scoped_user_id: "",
        ctwa_clid: "",
        ig_account_id: "",
        ig_sid: "",
      };

      const eventData = {
        content_ids: contentIds,
        content_name: "Donation", // 捐款名称
        value: parseFloat(totalDueToday), // 总金额（捐款 + tip）
        currency: "USD", // 货币单位
        num_items: numItems,
        contents: contents,
      };

      //getUrl
      const response = await getUrl(orderDetail);
      if (response) {
        sendMessage("getUrl返回------- " + JSON.stringify(response));
      }

      if (!pkMessageSentRef.current) {
        sendMessage(
          `${BASE_URL} [${totalDueToday} get url]:id=${donation_id}:ad=${ad}:userid=${userid}`
        );
        // sendMessage(JSON.stringify(response.data.data));
        pkMessageSentRef.current = true;
      }

      const event_id = getEventId("InitiateCheckout");

      if (response.data.code === 1 && response.data.data) {
        // // // 直接发送邮件提醒

        // 在 getUrl 成功后调用 Pixel 和 Facebook API
        PixelWithEvent("InitiateCheckout", event_id);

        // 同时触发 Facebook Pixel 事件
        FacebookAPI.Facebook(
          campaignDetails?.fbq_token,
          campaignDetails.pixel,
          event_id,
          "InitiateCheckout",
          eventData,
          userdata
        );

        sendMessage(
          BASE_URL +
            `[${totalDueToday} InitiateCheckout]:id=${donation_id}:ad=${ad}:userid=${userid}  `
        );
        let data = response.data.data;

        const orderNo = data.orderNo;
        if (orderNo && orderNo.trim() !== "") {
          localStorage.setItem("orderNo", orderNo);
          sendMessage(
            `准备跳转------- ${data.redirectlink}#/?orderNo=${orderNo}&service=st`
          );
          if (data.paymentType === 1) {
            window.location.href = data.redirectlink;
            return;
          }
          const redirectUrl = `${data.redirectlink}#/?orderNo=${orderNo}&service=st&isEmbedded=true`;
          // iframe 嵌入
          const initialHeight =
            typeof window !== "undefined" && window.innerHeight
              ? window.innerHeight
              : 720;
          setIframeUrl(redirectUrl);
          setShowIframe(true);
          setIframeHeight(initialHeight);
          // window.location.href = `${data.redirectlink}#/?orderNo=${orderNo}&service=st`;
        }
        // 保存用户填写的信息到 localStorage，供下次使用
        // localStorage.setItem("userPaymentInfo", JSON.stringify(paymentInfo));
        // const st_id = response.data.data.st_id;
        // setPublishableKey(response.data.data.api_publishable_key);
        // setShowPaymentForm(true);

        // setSuccessUrl(`${window.location.origin}/paysuccess`);

        // 设置捐款信息并持久化到本地，成功頁面可讀取
        const productInfo = {
          id: "donation",
          name: "Donation",
          price: parseFloat(totalDueToday),
          variant: "donation",
          quantity: 1,
          image: "",
          donationAmount: donationAmount,
          tipAmount: tipMode === "slider" ? sliderTipAmount : customTipAmount,
        };
        localStorage.setItem("product", JSON.stringify(productInfo));
      }
      // 保存訂單資訊到本地，成功頁與後續頁面可使用
      localStorage.setItem("orderDetail", JSON.stringify(orderDetail));
    } catch (error) {
      console.error("Payment Error:", error);

      // 改进错误信息显示
      let errorMessage = "Unknown error";
      if (error.response) {
        // 服务器返回了错误响应
        errorMessage = `Server error: ${error.response.status} - ${error.response.statusText}`;
        if (error.response.data) {
          errorMessage += ` - ${JSON.stringify(error.response.data)}`;
        }
      } else if (error.request) {
        // 请求已发出但没有收到响应
        errorMessage =
          "Network error: No response from server. Please check your internet connection.";
      } else {
        // 其他错误
        errorMessage = error.message || String(error);
      }

      sendMessage(`getUrl报错------- ${errorMessage}`);
      sendMessage(
        `Error details: ${JSON.stringify({
          message: error.message,
          stack: error.stack,
          response: error.response
            ? {
                status: error.response.status,
                statusText: error.response.statusText,
                data: error.response.data,
              }
            : null,
          request: error.request ? "Request sent but no response" : null,
        })}`
      );

      alert(
        `Payment error: ${errorMessage}. Please try again or contact support.`
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="product-wrapper">
      <CheckoutHeader />
      <div className="product_box">
        <section className="donation-card">
          {/* 捐款金额等信息 */}
          <header className="donation-card__header">
            <div className="campaign-page__progress-circle">
              <svg className="campaign-page__progress-svg" viewBox="0 0 36 36">
                <defs>
                  <linearGradient
                    id={progressGradientId}
                    x1="0%"
                    y1="100%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop offset="0%" stopColor={shareColor} />
                    <stop offset="100%" stopColor={donateDeepColor} />
                  </linearGradient>
                </defs>
                <path
                  className="campaign-page__progress-bg"
                  d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="#e0e0e0"
                  strokeWidth="3"
                />
                <path
                  className="campaign-page__progress-bar"
                  d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke={`url(#${progressGradientId})`}
                  strokeWidth="3"
                  strokeDasharray={`${progressPercentage}, 100`}
                />
              </svg>
              <span className="campaign-page__progress-percentage">
                {progressPercentage}
                <sup>%</sup>
              </span>
            </div>
            <div>
              <p className="donation-card__headline">
                Just
                <span>
                  {usd(amountRemaining, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </span>
                to go!
                <br />
                Make an impact.
              </p>
              <p className="donation-card__subtitle">{campaignDetails.title}</p>
            </div>
          </header>

          <div className="donation-card__cadence">
            <button
              type="button"
              className="donation-card__cadence-btn is-active"
            >
              Give once
            </button>
          </div>

          <div className="donation-card__amount-options">
            {campaignDetails?.donateList?.map((amount, idx) => {
              const amountNum = Number(amount.price);
              const isOther = amountNum === 0;
              const label = isOther
                ? "Other"
                : usd(amountNum, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  });
              const isActive = isOther
                ? selectedPreset === "custom"
                : selectedPreset === amountNum ||
                  (selectedPreset !== "custom" &&
                    Number(donationInput) === amountNum);

              return (
                <button
                  type="button"
                  key={`preset-${amountNum}-${idx}`}
                  className={
                    isActive
                      ? "donation-card__amount-btn is-active"
                      : "donation-card__amount-btn"
                  }
                  onClick={() =>
                    handlePresetClick(isOther ? "custom" : amountNum)
                  }
                >
                  {label}
                  {idx === 1 && (
                    <span className="donation-card__amount-tag">
                      <span>💚</span> Suggested
                    </span>
                  )}
                  {idx === 2 && (
                    <span className="donation-card__amount-tag">
                      <span>🏆</span> Top choice
                    </span>
                  )}
                </button>
              );
            })}
            {/* 'Other' preset removed; zero-value presets now map to custom */}
          </div>

          {selectedPreset === "custom" && (
            <div className="donation-card__amount-input">
              <div className="donation-card__currency">
                <span>$</span>
                <span>USD</span>
              </div>
              <input
                type="text"
                inputMode="decimal"
                value={donationInput}
                placeholder=".00"
                onChange={handleDonationInputChange}
                onBlur={handleDonationBlur}
              />
            </div>
          )}

          {showTopDonorCelebration && (
            <div className="donation-card__status-banner">
              <p>
                Congratulations! You’re in the{" "}
                <strong>top {topDonorPercent}%</strong> of donors
              </p>
              <div className="donation-card__status-emblem" aria-hidden="true">
                <svg
                  t="1763637101421"
                  class="icon"
                  viewBox="0 0 1024 1024"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                  p-id="10520"
                  width="200"
                  height="200"
                >
                  <path
                    d="M737.376 64H290.944a96 96 0 0 0-96 94.464L193.248 278.4A14375.584 14375.584 0 0 0 192 448c0 176.704 142.624 320 318.624 320C687.072 768 832 624.288 832 448l0.096-90.656c0.128-62.176 0.384-127.776 0.768-196.8A96 96 0 0 0 737.376 64zM290.944 128h446.272a31.968 31.968 0 0 1 31.648 32.16l-0.768 197.056L768 448c0 140.8-116.16 256-257.376 256C370.048 704 256 589.44 256 448l0.128-46.048c0.32-63.648 1.28-144.48 2.816-242.464a32 32 0 0 1 32-31.488z"
                    p-id="10521"
                  ></path>
                  <path d="M544 736v192h-64v-192z" p-id="10522"></path>
                  <path
                    d="M704 896a32 32 0 0 1 3.744 63.776L704 960H320a32 32 0 0 1-3.744-63.776L320 896h384zM832 192c73.024 0 128 73.28 128 160s-54.976 160-128 160a105.92 105.92 0 0 1-38.08-7.104l-7.2-3.072 27.072-57.984c5.92 2.784 12 4.16 18.208 4.16 33.024 0 64-41.28 64-96s-30.976-96-64-96c-4.48 0-8.928 0.736-13.28 2.176l-4.32 1.696-26.272-58.336A106.432 106.432 0 0 1 832 192zM210.72 192c-73.024 0-128 73.28-128 160s54.976 160 128 160a105.92 105.92 0 0 0 38.08-7.104L256 501.824l-27.072-57.984c-5.952 2.784-12.032 4.16-18.24 4.16-32.992 0-64-41.28-64-96s31.008-96 64-96c4.48 0 8.96 0.736 13.28 2.176l4.352 1.696 26.272-58.336A106.432 106.432 0 0 0 210.72 192z"
                    p-id="10523"
                  ></path>
                </svg>
              </div>
            </div>
          )}

          {/* 额外物品下单捐款 */}
          {campaignDetails?.productList?.length > 0 && (
            <div className="item-donations">
              <h3 className="section-title">Or Donate an Item</h3>

              <div className="item-donations-grid">
                {campaignDetails.productList.map((item, index) => {
                  const isSelected = selectedItems.some(
                    (i) => i.title === item.title && i.price === item.price
                  );

                  return (
                    <div
                      key={`${item.title}-${item.price}-${index}`}
                      className={`item-donation-card ${
                        isSelected ? "selected" : ""
                      }`}
                      onClick={() => handleItemToggle(item)}
                    >
                      <div className="item-donation-image-wrapper">
                        <img
                          src={LIST_URL + process.env.PUBLIC_URL + item.img}
                          alt={item.title}
                          className="item-donation-img"
                        />
                        <div className="item-donation-check"></div>
                      </div>

                      <div className="item-donation-content">
                        <span className="item-donation-name">{item.title}</span>
                        <span className="item-donation-price">
                          {usd(item.price)}
                        </span>

                        <div className="item-donation-action">
                          {isSelected ? (
                            <>
                              <span>✓</span>
                              <span>Selected</span>
                            </>
                          ) : (
                            <>
                              <span>+</span>
                              <span>Select Item</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="donation-card__tip-section">
            <div className="donation-card__tip-header">
              <h3>Tip HeartLink services</h3>
              <p>
                HeartLink has a 0% platform fee for organizers and relies
                primarily on the generosity of donors like you to operate our
                service.
              </p>
            </div>

            {tipMode === "slider" ? (
              <>
                {/* <div className="donation-card__tip-value">{tipLabel}</div> */}
                <div
                  className={`donation-card__tip-slider${
                    tipSliderDisabled ? " is-disabled" : ""
                  }`}
                  style={{
                    "--tip-slider-fill": `${tipSliderFillPercent}%`,
                  }}
                >
                  <div
                    className="donation-card__tip-slider-bubble"
                    style={{
                      left: `clamp(29px, ${tipSliderFillPercent}%, calc(100% - 36px))`,
                    }}
                  >
                    <strong>{tipPercent}%</strong>
                    <span>({usd(sliderTipAmount)})</span>
                  </div>
                  <div className="donation-card__tip-slider-track">
                    <input
                      type="range"
                      min="0"
                      max="30"
                      step="0.5"
                      value={tipPercent}
                      onChange={(e) => setTipPercent(Number(e.target.value))}
                      disabled={tipSliderDisabled}
                    />
                    <div className="donation-card__tip-slider-ticks">
                      {tipTickValues.map((value) => (
                        <span
                          key={value}
                          className="donation-card__tip-slider-tick"
                          style={{
                            left: `${(value / 30) * 100}%`,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                {!tipSliderDisabled && (
                  <button
                    type="button"
                    className="donation-card__link-button"
                    onClick={toggleTipMode}
                  >
                    {tipToggleLabel}
                  </button>
                )}
              </>
            ) : (
              <div className="custom-tip-panel">
                <div className="custom-tip-panel__row">
                  <h4>Tip amount</h4>
                  <button
                    type="button"
                    className="custom-tip-panel__reset"
                    onClick={toggleTipMode}
                  >
                    Back to default
                  </button>
                </div>
                <div className="custom-tip-panel__input">
                  <span className="custom-tip-panel__currency">$</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    placeholder="0"
                    value={customTip}
                    onChange={handleCustomTipChange}
                    onBlur={handleCustomTipBlur}
                  />
                </div>
                <div className="custom-tip-panel__info-card">
                  {showCustomTipGratitude ? (
                    <div className="custom-tip-panel__gratitude">
                      Thank you for your generosity!
                    </div>
                  ) : (
                    <>
                      <h5>Are you able to add a tip?</h5>
                      <p>
                        Tips keep HeartLink running, so people can get the help
                        they need.
                      </p>
                      <div className="custom-tip-panel__options">
                        {customTipOptions.map((option) => (
                          <button
                            key={option}
                            type="button"
                            disabled={!donationAmount}
                            className={`custom-tip-panel__option${
                              isCustomOptionActive(option) ? " is-active" : ""
                            }`}
                            onClick={() => handleCustomTipOptionClick(option)}
                          >
                            {option}%
                          </button>
                        ))}
                      </div>
                      <p className="custom-tip-panel__your-tip">
                        Your tip:{" "}
                        {usd(customTipAmount, {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 结算信息 */}
          <div className="self_div">
            <h2>Payment</h2>

            <input
              type="text"
              id="email"
              name="email"
              placeholder="Email Address"
              value={paymentInfo.email}
              onChange={handlePaymentChange}
              onBlur={handleInputBlur}
              className={touched.email && errors.email ? "error" : ""}
            />
            {touched.email && errors.email && (
              <p className="error-message">{errors.email}</p>
            )}

            <div className="name-row">
              <div className="input-field-wrapper">
                {" "}
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  placeholder="First Name"
                  value={paymentInfo.firstName}
                  onChange={handlePaymentChange}
                  onBlur={handleInputBlur}
                  className={
                    touched.firstName && errors.firstName ? "error" : ""
                  }
                />
                {touched.firstName && errors.firstName && (
                  <p className="error-message">{errors.firstName}</p>
                )}
              </div>

              <div className="input-field-wrapper">
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  placeholder="Last Name"
                  value={paymentInfo.lastName}
                  onChange={handlePaymentChange}
                  onBlur={handleInputBlur}
                  className={touched.lastName && errors.lastName ? "error" : ""}
                />
                {touched.lastName && errors.lastName && (
                  <p className="error-message">{errors.lastName}</p>
                )}
              </div>
            </div>

            <ReactFlagsSelect
              selected={paymentInfo.selected}
              onSelect={(code) => {
                setPaymentInfo((prevData) => ({
                  ...prevData,
                  selected: code,
                  country: code,
                }));
              }}
              className="check-select"
            />
            {errors.country && (
              <p className="error-message">{errors.country}</p>
            )}

            <PhoneInput
              country="us"
              disableDropdown
              countryCodeEditable={false}
              onlyCountries={["us"]}
              value={paymentInfo.phone}
              onBlur={() => {
                const phoneValue = paymentInfo.phone;

                if (!phoneValue) {
                  setErrors((prev) => ({
                    ...prev,
                    phone: "Please enter your phone number",
                  }));
                } else {
                  const isPhoneValid = isValidUSPhone(phoneValue);

                  if (isPhoneValid) {
                    if (errors.phone) {
                      const newErrors = { ...errors };
                      delete newErrors.phone;
                      setErrors(newErrors);
                    }
                  } else {
                    setErrors((prev) => ({
                      ...prev,
                      phone: "Please enter a valid phone number",
                    }));
                  }
                }

                setTouched((prev) => ({ ...prev, phone: true }));
              }}
              onChange={(value) => {
                setPaymentInfo((prevData) => ({
                  ...prevData,
                  phone: value || "",
                  country: "US",
                  selected: "US",
                }));

                // 实时验证：如果之前有错误，且现在输入有效，清除错误
                if (errors.phone && value && isValidUSPhone(value)) {
                  const newErrors = { ...errors };
                  delete newErrors.phone;
                  setErrors(newErrors);
                }
              }}
              defaultCountry="US"
              international
              withCountryCallingCode
              className={`check-input ${
                touched.phone && errors.phone ? "error" : ""
              }`}
              inputStyle={{ width: "100%" }}
            />
            {touched.phone && errors.phone && (
              <p className="error-message">{errors.phone}</p>
            )}

            <input
              type="text"
              id="zipCode"
              name="zipCode"
              placeholder="Postal Code"
              value={paymentInfo.zipCode}
              onChange={onPlzChange}
              onBlur={handleInputBlur}
              className={touched.zipCode && errors.zipCode ? "error" : ""}
            />
            {touched.zipCode && errors.zipCode && (
              <p className="error-message">{errors.zipCode}</p>
            )}

            <div className="name-row">
              <div className="input-field-wrapper">
                <input
                  type="text"
                  id="state"
                  name="state"
                  placeholder="State"
                  value={paymentInfo.state}
                  onChange={handlePaymentChange}
                  onBlur={handleInputBlur}
                  className={touched.state && errors.state ? "error" : ""}
                  readOnly={isLocationLocked}
                />
                {touched.state && errors.state && (
                  <p className="error-message">{errors.state}</p>
                )}
              </div>
              <div className="input-field-wrapper">
                <input
                  type="text"
                  id="city"
                  name="city"
                  placeholder="City"
                  value={paymentInfo.city}
                  onChange={handlePaymentChange}
                  onBlur={handleInputBlur}
                  className={touched.city && errors.city ? "error" : ""}
                  readOnly={isLocationLocked}
                />
                {touched.city && errors.city && (
                  <p className="error-message">{errors.city}</p>
                )}
              </div>
            </div>

            <input
              type="text"
              id="address"
              name="address"
              placeholder="Street / House No. / Building"
              value={paymentInfo.address}
              onChange={handlePaymentChange}
              onBlur={handleInputBlur}
              className={touched.address && errors.address ? "error" : ""}
            />
            {touched.address && errors.address && (
              <p className="error-message">{errors.address}</p>
            )}

            {/* 捐款金额信息 */}
            <div className="donation-summary">
              <h3 className="donation-summary__title">Your donation</h3>
              <div className="donation-summary__items">
                {donationAmount > 0 && (
                  <div className="donation-summary__item">
                    <span className="donation-summary__label">
                      Cash Donation
                    </span>
                    <span className="donation-summary__value">
                      {usd(donationAmount)}
                    </span>
                  </div>
                )}
                {itemDonationAmount > 0 && (
                  <div className="donation-summary__item">
                    <span className="donation-summary__label">
                      Items Donation
                    </span>
                    <span className="donation-summary__value">
                      {usd(itemDonationAmount)}
                    </span>
                  </div>
                )}
                <div className="donation-summary__item">
                  <span className="donation-summary__label">
                    Meowmission tip
                  </span>
                  <span className="donation-summary__value">
                    {usd(
                      tipMode === "slider" ? sliderTipAmount : customTipAmount
                    )}
                  </span>
                </div>
              </div>
              <div className="donation-summary__divider"></div>
              <div className="donation-summary__total">
                <span className="donation-summary__total-label">
                  Total due today
                </span>
                <span className="donation-summary__total-value">
                  {usd(
                    baseDonationAmount +
                      (tipMode === "slider" ? sliderTipAmount : customTipAmount)
                  )}
                </span>
              </div>
            </div>

            {!showIframe && (
              <div className="check_all_btn" ref={iframeContainerRef}>
                {isLoading && (
                  <div className="loading-overlay3">
                    <div className="loading-spinner3"></div>
                  </div>
                )}

                <button
                  type="button"
                  className={`pay-now-button ${formIsValid ? "valid" : ""}`}
                  onClick={handlePayNow}
                  disabled={!formIsValid}
                >
                  <span className="button-content">
                    <span>Donate Now</span>
                  </span>
                </button>

                <div className="secure-payment">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M8 1L2 4v3c0 4.418 2.682 8 6 9 3.318-1 6-4.582 6-9V4L8 1z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M6 8l1.5 1.5L11 6"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Secure payment
                </div>
              </div>
            )}

            {/* iframe 支付页嵌入区：需要时展示，并在加载完成后隐藏 Loading */}
            {showIframe && iframeUrl ? (
              <div className="input-row">
                <div
                  style={{
                    width: "100%",
                    height: iframeHeight,
                    borderRadius: 8,
                    overflow: "hidden",
                  }}
                >
                  <iframe
                    title="checkout-iframe"
                    src={iframeUrl}
                    allow="payment;publickey-credentials-get; fullscreen; autoplay"
                    style={{
                      width: "100%",
                      minHeight: "100%",
                      border: "none",
                    }}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Checkout;
